import React, { Component } from 'react';
import { Button, Alert, Badge, Card, CardBody, CardTitle, Nav, NavItem, NavLink, TabContent, TabPane, Table, Modal, ModalHeader, ModalBody, ModalFooter, Input, FormGroup, Label } from 'reactstrap';
import { Navigate } from 'react-router-dom';

export class ServicePage extends Component {
  static displayName = ServicePage.name;

  constructor(props) {
    super(props);
    this.state = {
      orders: [],
      transactions: [],
      loading: true,
      user: null,
      redirectToLogin: false,
      error: null,
      success: null,
      activeTab: 'orders',
      transactionStatusFilter: null,
      // Modal state
      actionModalOpen: false,
      actionType: null, // 'collect', 'cancel', 'return'
      selectedTransaction: null,
      actionNotes: ''
    };
  }

  componentDidMount() {
    this.loadUser();
    this.loadOrders();
    this.loadTransactions();
    // Auto-refresh every 10 seconds
    this.interval = setInterval(() => {
      this.loadOrders();
      this.loadTransactions();
    }, 10000);
  }

  componentWillUnmount() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  loadUser() {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        // Check if user has service role (role = 1) or admin role (role = 2)
        if (user.role !== 1 && user.role !== 2) {
          this.setState({ redirectToLogin: true, error: 'Access denied. Service or Admin role required.' });
        } else {
          this.setState({ user });
        }
      } catch (e) {
        console.error('Error parsing user data:', e);
        this.setState({ redirectToLogin: true });
      }
    } else {
      this.setState({ redirectToLogin: true });
    }
  }

  async loadOrders() {
    try {
      const response = await fetch('api/coffeeorder/orders');
      if (response.ok) {
        const data = await response.json();
        // Sort by status (active orders first) then by date
        const sortedOrders = data.sort((a, b) => {
          if (a.status === 3 && b.status !== 3) return 1; // Received orders last
          if (a.status !== 3 && b.status === 3) return -1;
          return new Date(b.orderDate) - new Date(a.orderDate);
        });
        this.setState({ orders: sortedOrders, loading: false });
      }
    } catch (error) {
      console.error('Error loading orders:', error);
      this.setState({ loading: false });
    }
  }

  async loadTransactions() {
    const { user, transactionStatusFilter } = this.state;
    if (!user) return;

    try {
      let url = 'api/transaction';
      if (transactionStatusFilter !== null) {
        url += `?status=${transactionStatusFilter}`;
      }

      const response = await fetch(url, {
        headers: {
          'X-User-Id': user.userId
        }
      });

      if (response.ok) {
        const data = await response.json();
        this.setState({ transactions: data });
      }
    } catch (error) {
      console.error('Error loading transactions:', error);
    }
  }

  getTransactionStatusName(status) {
    const statuses = ['Acknowledged', 'Collected', 'Cancelled', 'Returned'];
    return statuses[status] || 'Unknown';
  }

  getTransactionStatusBadgeColor(status) {
    const colors = ['warning', 'success', 'danger', 'info'];
    return colors[status] || 'secondary';
  }

  openActionModal = (transaction, actionType) => {
    this.setState({
      actionModalOpen: true,
      selectedTransaction: transaction,
      actionType: actionType,
      actionNotes: ''
    });
  }

  closeActionModal = () => {
    this.setState({
      actionModalOpen: false,
      selectedTransaction: null,
      actionType: null,
      actionNotes: ''
    });
  }

  handleTransactionAction = async () => {
    const { user, selectedTransaction, actionType, actionNotes } = this.state;
    if (!user || !selectedTransaction) return;

    try {
      const response = await fetch(`api/transaction/${selectedTransaction.id}/${actionType}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': user.userId
        },
        body: JSON.stringify({ notes: actionNotes })
      });

      if (response.ok) {
        this.setState({
          success: `Transaction ${actionType}ed successfully!`,
          error: null
        });
        this.closeActionModal();
        await this.loadTransactions();
        await this.loadOrders();
      } else {
        const data = await response.json();
        this.setState({ error: data.message || `Failed to ${actionType} transaction` });
      }
    } catch (error) {
      console.error(`Error ${actionType}ing transaction:`, error);
      this.setState({ error: `Failed to ${actionType} transaction` });
    }
  }

  setActiveTab = (tab) => {
    this.setState({ activeTab: tab });
  }

  setTransactionStatusFilter = (status) => {
    this.setState({ transactionStatusFilter: status }, () => {
      this.loadTransactions();
    });
  }

  getCoffeeTypeName(type) {
    const types = ['Latte', 'Americano'];
    return types[type] || 'Unknown';
  }

  getTemperatureName(temp) {
    const temps = ['Hot', 'Cold'];
    return temps[temp] || 'Unknown';
  }

  getMilkPreferenceName(milk) {
    const milks = ['None', 'Whole Milk', 'Skim Milk', 'Almond Milk', 'Oat Milk', 'Soy Milk'];
    return milks[milk] || 'Unknown';
  }

  getOrderStatusName(status) {
    const statuses = ['Ordered', 'In Progress', 'Done', 'Received', 'Cancelled'];
    return statuses[status] || 'Unknown';
  }

  getOrderStatusBadgeColor(status) {
    const colors = ['secondary', 'warning', 'success', 'info', 'danger'];
    return colors[status] || 'secondary';
  }

  handleStatusChange = async (orderId, newStatus) => {
    try {
      const response = await fetch(`api/coffeeorder/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        await this.loadOrders();
      } else {
        console.error('Failed to update order status');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  handleCancelOrder = async (orderId) => {
    const { user } = this.state;

    if (!user) {
      this.setState({ error: 'You must be logged in to cancel orders' });
      return;
    }

    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }

    try {
      const response = await fetch(`api/coffeeorder/orders/${orderId}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.userId,
          userRole: user.role || 1
        })
      });

      if (response.ok) {
        await this.loadOrders();
        this.setState({ error: null });
      } else {
        const errorData = await response.json();
        this.setState({ error: errorData.message || 'Failed to cancel order' });
      }
    } catch (error) {
      console.error('Error cancelling order:', error);
      this.setState({ error: 'Failed to cancel order' });
    }
  };

  getStatusCounts() {
    const { orders } = this.state;
    return {
      ordered: orders.filter(o => o.status === 0).length,
      inProgress: orders.filter(o => o.status === 1).length,
      done: orders.filter(o => o.status === 2).length,
      received: orders.filter(o => o.status === 3).length,
      cancelled: orders.filter(o => o.status === 4).length
    };
  }

  renderOrderCard(order) {
    return (
      <Card key={order.id} className="mb-3">
        <CardBody>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <CardTitle tag="h5">
                Order #{order.id} - {this.getCoffeeTypeName(order.coffeeType)}
              </CardTitle>
              <p className="mb-1">
                <strong>Customer:</strong> {order.customerName || 'Anonymous'}
              </p>
              <p className="mb-1">
                <strong>Options:</strong> {this.getTemperatureName(order.temperature)}
                {order.isDecaf && ', Decaf'}
                {order.milkPreference !== 0 && `, ${this.getMilkPreferenceName(order.milkPreference)}`}
              </p>
              <p className="mb-1">
                <strong>Price:</strong> ${order.totalPrice.toFixed(2)} {order.isPaid ? '(Paid ✓)' : '(Not Paid)'}
              </p>
              <p className="mb-1 text-muted">
                <small>Ordered: {new Date(order.orderDate).toLocaleString()}</small>
              </p>
            </div>
            <div className="text-right">
              <Badge color={this.getOrderStatusBadgeColor(order.status)} className="mb-2">
                {this.getOrderStatusName(order.status)}
              </Badge>
              <div>
                {order.status === 0 && (
                  <>
                    <Button
                      size="sm"
                      color="warning"
                      onClick={() => this.handleStatusChange(order.id, 1)}
                      block
                      className="mb-1"
                    >
                      Start Preparing
                    </Button>
                    <Button
                      size="sm"
                      color="danger"
                      onClick={() => this.handleCancelOrder(order.id)}
                      block
                      outline
                    >
                      Cancel Order
                    </Button>
                  </>
                )}
                {order.status === 1 && (
                  <>
                    <Button
                      size="sm"
                      color="success"
                      onClick={() => this.handleStatusChange(order.id, 2)}
                      block
                      className="mb-1"
                    >
                      Mark as Done
                    </Button>
                    <Button
                      size="sm"
                      color="danger"
                      onClick={() => this.handleCancelOrder(order.id)}
                      block
                      outline
                    >
                      Cancel Order
                    </Button>
                  </>
                )}
                {order.status === 2 && (
                  <>
                    <Button
                      size="sm"
                      color="info"
                      onClick={() => this.handleStatusChange(order.id, 3)}
                      block
                      className="mb-1"
                    >
                      Mark as Received
                    </Button>
                    <Button
                      size="sm"
                      color="danger"
                      onClick={() => this.handleCancelOrder(order.id)}
                      block
                      outline
                    >
                      Cancel Order
                    </Button>
                  </>
                )}
                {order.status === 4 && (
                  <span className="text-muted">Order Cancelled</span>
                )}
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  renderTransactionsTab() {
    const { transactions, transactionStatusFilter, actionModalOpen, actionType, actionNotes, selectedTransaction } = this.state;

    const transactionCounts = {
      acknowledged: transactions.filter(t => t.status === 0).length,
      collected: transactions.filter(t => t.status === 1).length,
      cancelled: transactions.filter(t => t.status === 2).length,
      returned: transactions.filter(t => t.status === 3).length
    };

    return (
      <div>
        {/* Transaction Status Filter */}
        <div className="mb-3">
          <Button
            color={transactionStatusFilter === null ? 'primary' : 'outline-primary'}
            size="sm"
            className="me-1"
            onClick={() => this.setTransactionStatusFilter(null)}
          >
            All ({transactions.length})
          </Button>
          <Button
            color={transactionStatusFilter === 0 ? 'warning' : 'outline-warning'}
            size="sm"
            className="me-1"
            onClick={() => this.setTransactionStatusFilter(0)}
          >
            Acknowledged ({transactionCounts.acknowledged})
          </Button>
          <Button
            color={transactionStatusFilter === 1 ? 'success' : 'outline-success'}
            size="sm"
            className="me-1"
            onClick={() => this.setTransactionStatusFilter(1)}
          >
            Collected ({transactionCounts.collected})
          </Button>
          <Button
            color={transactionStatusFilter === 2 ? 'danger' : 'outline-danger'}
            size="sm"
            className="me-1"
            onClick={() => this.setTransactionStatusFilter(2)}
          >
            Cancelled ({transactionCounts.cancelled})
          </Button>
          <Button
            color={transactionStatusFilter === 3 ? 'info' : 'outline-info'}
            size="sm"
            onClick={() => this.setTransactionStatusFilter(3)}
          >
            Returned ({transactionCounts.returned})
          </Button>
        </div>

        {/* Transactions Table */}
        <Table responsive striped>
          <thead>
            <tr>
              <th>ID</th>
              <th>Order</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Description</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map(transaction => (
              <tr key={transaction.id}>
                <td><small>{transaction.id.substring(0, 8)}...</small></td>
                <td>{transaction.orderId ? `#${transaction.orderId}` : '-'}</td>
                <td>
                  ${transaction.amount.toFixed(2)}
                  {transaction.currencySymbol && transaction.currencySymbol !== '$' && (
                    <small className="text-muted d-block">
                      ({transaction.currencySymbol}{transaction.paidAmount.toFixed(2)})
                    </small>
                  )}
                </td>
                <td>
                  <Badge color={this.getTransactionStatusBadgeColor(transaction.status)}>
                    {this.getTransactionStatusName(transaction.status)}
                  </Badge>
                </td>
                <td><small>{transaction.description || '-'}</small></td>
                <td><small>{new Date(transaction.createdAt).toLocaleString()}</small></td>
                <td>
                  {transaction.status === 0 && (
                    <>
                      <Button
                        size="sm"
                        color="success"
                        className="me-1"
                        onClick={() => this.openActionModal(transaction, 'collect')}
                      >
                        Collect
                      </Button>
                      <Button
                        size="sm"
                        color="danger"
                        outline
                        onClick={() => this.openActionModal(transaction, 'cancel')}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                  {transaction.status === 1 && (
                    <Button
                      size="sm"
                      color="info"
                      onClick={() => this.openActionModal(transaction, 'return')}
                    >
                      Return/Refund
                    </Button>
                  )}
                  {(transaction.status === 2 || transaction.status === 3) && (
                    <span className="text-muted">No actions available</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {transactions.length === 0 && (
          <Alert color="info">No transactions found.</Alert>
        )}

        {/* Action Modal */}
        <Modal isOpen={actionModalOpen} toggle={this.closeActionModal}>
          <ModalHeader toggle={this.closeActionModal}>
            {actionType === 'collect' && 'Collect Transaction'}
            {actionType === 'cancel' && 'Cancel Transaction'}
            {actionType === 'return' && 'Return/Refund Transaction'}
          </ModalHeader>
          <ModalBody>
            {selectedTransaction && (
              <div className="mb-3">
                <p><strong>Transaction:</strong> {selectedTransaction.id.substring(0, 8)}...</p>
                <p><strong>Amount:</strong> ${selectedTransaction.amount.toFixed(2)}</p>
                <p><strong>Description:</strong> {selectedTransaction.description || 'N/A'}</p>
                {actionType === 'cancel' && (
                  <Alert color="warning">
                    Cancelling this transaction will restore ${selectedTransaction.amount.toFixed(2)} to the customer's balance.
                  </Alert>
                )}
                {actionType === 'return' && (
                  <Alert color="info">
                    Returning this transaction will refund ${selectedTransaction.amount.toFixed(2)} to the customer's balance.
                  </Alert>
                )}
              </div>
            )}
            <FormGroup>
              <Label for="actionNotes">Notes (optional)</Label>
              <Input
                type="textarea"
                id="actionNotes"
                value={actionNotes}
                onChange={(e) => this.setState({ actionNotes: e.target.value })}
                placeholder="Add any notes about this action..."
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button
              color={actionType === 'cancel' ? 'danger' : actionType === 'return' ? 'info' : 'success'}
              onClick={this.handleTransactionAction}
            >
              Confirm {actionType}
            </Button>
            <Button color="secondary" onClick={this.closeActionModal}>Cancel</Button>
          </ModalFooter>
        </Modal>
      </div>
    );
  }

  render() {
    const { loading, user, redirectToLogin, error, success, orders, activeTab } = this.state;

    if (redirectToLogin) {
      return <Navigate to="/login" state={{ error }} />;
    }

    if (loading) {
      return <p><em>Loading...</em></p>;
    }

    const counts = this.getStatusCounts();
    const activeOrders = orders.filter(o => o.status !== 3 && o.status !== 4);
    const completedOrders = orders.filter(o => o.status === 3);
    const cancelledOrders = orders.filter(o => o.status === 4);

    return (
      <div className="service-page">
        <div className="text-center mb-4">
          <h1 style={{ fontFamily: 'var(--font-display)', color: 'var(--moss-dark)' }}>
            Service Dashboard
          </h1>
          <p className="lead" style={{ maxWidth: '500px', margin: '0 auto' }}>
            Welcome, {user?.username}! Keep the coffee flowing.
          </p>
        </div>

        {error && (
          <Alert color="danger" toggle={() => this.setState({ error: null })}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert color="success" toggle={() => this.setState({ success: null })}>
            {success}
          </Alert>
        )}

        {/* Navigation Tabs */}
        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink
              className={activeTab === 'orders' ? 'active' : ''}
              onClick={() => this.setActiveTab('orders')}
              style={{ cursor: 'pointer' }}
            >
              Orders
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 'transactions' ? 'active' : ''}
              onClick={() => this.setActiveTab('transactions')}
              style={{ cursor: 'pointer' }}
            >
              Transactions
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          {/* Orders Tab */}
          <TabPane tabId="orders">
            {/* Status Summary */}
            <div className="row mb-4">
              <div className="col-md-2">
                <Card className="text-center">
                  <CardBody>
                    <h3 className="text-secondary">{counts.ordered}</h3>
                    <p className="mb-0">New Orders</p>
                  </CardBody>
                </Card>
              </div>
              <div className="col-md-2">
                <Card className="text-center">
                  <CardBody>
                    <h3 className="text-warning">{counts.inProgress}</h3>
                    <p className="mb-0">In Progress</p>
                  </CardBody>
                </Card>
              </div>
              <div className="col-md-2">
                <Card className="text-center">
                  <CardBody>
                    <h3 className="text-success">{counts.done}</h3>
                    <p className="mb-0">Ready</p>
                  </CardBody>
                </Card>
              </div>
              <div className="col-md-2">
                <Card className="text-center">
                  <CardBody>
                    <h3 className="text-info">{counts.received}</h3>
                    <p className="mb-0">Completed</p>
                  </CardBody>
                </Card>
              </div>
              <div className="col-md-2">
                <Card className="text-center">
                  <CardBody>
                    <h3 className="text-danger">{counts.cancelled}</h3>
                    <p className="mb-0">Cancelled</p>
                  </CardBody>
                </Card>
              </div>
            </div>

            {/* Active Orders */}
            <h3 style={{ fontFamily: 'var(--font-display)', color: 'var(--moss-dark)' }}>
              Active Orders ({activeOrders.length})
            </h3>
            {activeOrders.length === 0 ? (
              <Alert color="info">No active orders at the moment.</Alert>
            ) : (
              <div className="mb-4">
                {activeOrders.map(order => this.renderOrderCard(order))}
              </div>
            )}

            {/* Completed Orders */}
            {completedOrders.length > 0 && (
              <>
                <h3 style={{ fontFamily: 'var(--font-display)', color: 'var(--moss-dark)', marginTop: '2rem' }}>
                  Completed Orders ({completedOrders.length})
                </h3>
                <div className="mb-4">
                  {completedOrders.slice(0, 10).map(order => this.renderOrderCard(order))}
                </div>
              </>
            )}

            {/* Cancelled Orders */}
            {cancelledOrders.length > 0 && (
              <>
                <h3 style={{ fontFamily: 'var(--font-display)', color: 'var(--moss-dark)', marginTop: '2rem' }}>
                  Cancelled Orders ({cancelledOrders.length})
                </h3>
                <div className="mb-4">
                  {cancelledOrders.slice(0, 10).map(order => this.renderOrderCard(order))}
                </div>
              </>
            )}
          </TabPane>

          {/* Transactions Tab */}
          <TabPane tabId="transactions">
            <h3 style={{ fontFamily: 'var(--font-display)', color: 'var(--moss-dark)' }}>
              Transaction Ledger
            </h3>
            <p className="text-muted mb-4">
              Manage payment transactions. Collect when orders are completed, cancel for refunds before collection, or return for refunds after collection.
            </p>
            {this.renderTransactionsTab()}
          </TabPane>
        </TabContent>
      </div>
    );
  }
}

